import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import appcolors from "../../../constants/Colors";
import { submissionStatusColor } from "./CampaignVideoSubmission";

// Purane campaigns me range seedha KPI pe thi
export const getKpiTiers = (kpi) =>
  kpi?.tiers?.length ? kpi.tiers : kpi?.minViews !== undefined ? [kpi] : [];

const statusColor = {
  pending: "warning",
  accepted: "success",
  rejected: "error",
};

// Participate button / request status - card aur detail page dono me use hota hai
export function CampaignParticipationAction({
  campaign,
  onParticipate,
  isRequesting,
}) {
  const { t } = useTranslation();
  const participation = campaign?.participation;

  if (
    participation?.status === "pending" ||
    participation?.status === "accepted"
  ) {
    return (
      <Chip
        color={statusColor[participation.status]}
        label={t(
          participation.status === "pending"
            ? "campaignRequestPending"
            : "campaignRequestAccepted",
        )}
      />
    );
  }

  if (campaign?.status !== "published") return null;

  if (campaign?.isFull) {
    return <Chip label={t("campaignFull")} />;
  }

  return (
    <Stack gap={0.5} alignItems={"flex-start"}>
      {participation?.status === "rejected" && (
        <Typography variant="caption" color="error">
          {t("campaignRequestRejected")}
          {participation.rejectionReason
            ? `: ${participation.rejectionReason}`
            : ""}
        </Typography>
      )}
      <Button
        variant="contained"
        size="small"
        disabled={isRequesting}
        startIcon={
          isRequesting && <CircularProgress size={14} color="inherit" />
        }
        onClick={() => onParticipate(campaign)}
      >
        {t(
          participation?.status === "rejected"
            ? "campaignRequestAgain"
            : "participate",
        )}
      </Button>
    </Stack>
  );
}

function CampaignCard({ campaign, onParticipate, isRequesting, showAction }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const goToDetail = () => navigate(`/creator/campaigns/${campaign?._id}`);

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
      }}
    >
      <Box sx={{ cursor: "pointer" }} onClick={goToDetail}>
        {campaign?.coverImage ? (
          <Box
            component="img"
            src={campaign.coverImage}
            alt={campaign.name}
            sx={{ width: "100%", height: 170, objectFit: "cover" }}
          />
        ) : (
          <Stack
            height={170}
            alignItems="center"
            justifyContent="center"
            bgcolor={"#f4f4f4"}
          >
            <CampaignOutlinedIcon sx={{ fontSize: 60, color: "#bdbdbd" }} />
          </Stack>
        )}
      </Box>

      <CardContent
        sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <Typography
            variant="h6"
            fontSize={"1.05rem"}
            fontWeight={600}
            color={appcolors.gunmetal}
            sx={{ cursor: "pointer" }}
            onClick={goToDetail}
          >
            {campaign?.name}
          </Typography>
          <Chip size="small" label={t(campaign?.type)} />
        </Stack>

        <Stack direction="row" gap={2} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {t("maxCreators")}:{" "}
            {campaign?.maxCreators
              ? `${campaign?.acceptedParticipants ?? 0}/${campaign.maxCreators}`
              : t("unlimited")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("starterBoxes")}: {campaign?.packages?.length || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("commission")}: {campaign?.commission ?? 0}%
          </Typography>
        </Stack>

        <Stack gap={0.75}>
          <Typography variant="body2" fontWeight={600}>
            {t("kpis")}
          </Typography>
          {(campaign?.kpis || []).map((kpi) => (
            <Stack key={kpi.platform} gap={0.25}>
              <Typography variant="body2" fontWeight={600}>
                {t(kpi.platform)}
              </Typography>
              {getKpiTiers(kpi).map((tier, tierIndex) => (
                <Typography key={tierIndex} variant="body2" pl={1}>
                  •{" "}
                  {t("campaignKpiText", {
                    min: Number(tier.minViews).toLocaleString(),
                    max: Number(tier.maxViews).toLocaleString(),
                    days: tier.timeFrameDays,
                  })}{" "}
                  ({t("budget")}: {Number(tier.budget || 0).toLocaleString()}{" "}
                  RON)
                </Typography>
              ))}
            </Stack>
          ))}
        </Stack>

        <Box flex={1} />
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
        >
          {showAction ? (
            <CampaignParticipationAction
              campaign={campaign}
              onParticipate={onParticipate}
              isRequesting={isRequesting}
            />
          ) : campaign?.participation?.status === "accepted" ? (
            // Dashboard pe video ka status
            <Chip
              size="small"
              variant="outlined"
              color={
                submissionStatusColor[campaign.participation.submission?.status] ||
                "default"
              }
              label={t(
                campaign.participation.submission
                  ? `videoStatus_${campaign.participation.submission.status}`
                  : "videoStatus_notSubmitted",
              )}
            />
          ) : (
            <span />
          )}
          <Button size="small" variant="outlined" onClick={goToDetail} sx={{ height: 32 }}>
            {t("viewDetails")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default CampaignCard;
